import { useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import type { AtsJobApplicationConvertToEmployeePageFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { convertDate } from 'modules/core/lib/dates'
import { apiClient } from 'modules/core/lib/mobx-rest'

import UnifiedAddEmployeeModal from 'modules/employees/components/UnifiedAddEmployeeModal'
import { parseSalary } from 'modules/employees/components/UnifiedAddEmployeeModal/ContractInformationStep/salaryUtils'

interface OutletContext {
  application: AtsJobApplicationConvertToEmployeePageFragment
}

const JobApplicationConvertToEmployeePage = () => {
  const client = apiClient()
  const { application } = useOutletContext<OutletContext>()
  const [isSubmitting, setSubmitting] = useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const previousPath = pathname.replace('/convert-to-employee', '')

  const onClose = () => navigate(previousPath)

  const onSuccess = (employeeId: string) => {
    setSubmitting(true)
    client.cache.evict('employees')
    client.cache.evict('users')
    navigate(navigation.employees.id.fullPath({ id: employeeId }))
  }

  if (isSubmitting) return null

  const offer = application.offer

  const formatStartDate = (dateValue: string | null | undefined): string | undefined => {
    if (!dateValue) return undefined

    try {
      return convertDate(dateValue, 'backend', 'ui')
    } catch (error) {
      return undefined
    }
  }

  const salaryAmountString = offer?.salaryAmount?.toString()
  const parsedSalary = salaryAmountString ? parseSalary(salaryAmountString) : undefined
  const isSalaryValid = !!parsedSalary && parsedSalary >= 0

  return (
    <UnifiedAddEmployeeModal
      initialValues={{
        firstName: application.candidate.firstName,
        lastName: application.candidate.lastName,
        email: application.candidate.email || undefined,
        teamId: application.jobPosting.teamId?.toString(),
        workplaceId: application.jobPosting.locationId?.toString(),
        phoneNumber: offer?.candidatePhone || undefined,
        salaryAmount: salaryAmountString || undefined,
        isSalaryValid,
        contractsStartsOn: formatStartDate(offer?.startDate),
      }}
      source={{
        sourceType: 'ats_application',
        sourceId: application.id.toString(),
      }}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  )
}

export default JobApplicationConvertToEmployeePage
